import { Link } from "react-router-dom";
import { Flame, Clock, BadgeIndianRupee, ArrowRight, Phone, MessageCircle } from "lucide-react";
import { MENU, IMAGES, CONTACT } from "@/data/menu";

const WHY = [
  { icon: Flame, title: "Slidin' Deliciously", text: "Sauces and fillings loaded to the brim — every slider hits hard." },
  { icon: Clock, title: "Made-To-Order, Quick", text: "Fresh off the grill when you order. Never sitting under a heat lamp." },
  { icon: BadgeIndianRupee, title: "Fair Prices", text: "Honest food at honest prices. Sliders from just ₹139." },
];

const MENU_CARDS = [
  { key: "veg", label: "Veg Sliders", image: IMAGES.veg, blurb: "Paneer, pav bhaji, kimchi and more" },
  { key: "nonVeg", label: "Non-Veg Sliders", image: IMAGES.chicken, blurb: "Butter chicken, keema and cheesy chicken" },
  { key: "sides", label: "Sides", image: IMAGES.fries, blurb: "Fries and baked cheesy nachos" },
  { key: "dessert", label: "Dessert & Drinks", image: IMAGES.dessert, blurb: "Brownies, cold coffee and brews" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero — full width photo with dark overlay */}
      <section className="relative" data-testid="hero-section">
        <div className="relative h-[70vh] md:h-[85vh] overflow-hidden">
          <img src={IMAGES.hero} alt="GOAT slider" className="absolute inset-0 w-full h-full object-cover" data-testid="hero-image" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(2,68,35,0.85), rgba(2,68,35,0.25) 55%, rgba(0,0,0,0.15))" }} />
          <div className="relative max-w-7xl mx-auto px-4 md:px-8 h-full flex flex-col justify-end pb-14 md:pb-20">
            <span className="badge-sticker inline-block w-fit px-5 py-2 text-sm font-semibold uppercase tracking-widest mb-5" style={{ background: "var(--goat-orange)", color: "var(--goat-white)", borderColor: "var(--goat-white)", boxShadow: "3px 3px 0px var(--goat-white)" }} data-testid="hero-badge">
              The GOAT of Sliders
            </span>
            <h1 className="text-5xl md:text-7xl uppercase leading-none tracking-tighter" style={{ color: "var(--goat-cream)" }} data-testid="hero-heading">
              The Greatest Sliders<br />
              <span style={{ color: "var(--goat-orange)" }}>Of All Time</span>
            </h1>
            <p className="mt-4 text-lg md:text-xl max-w-md" style={{ color: "var(--goat-cream)" }} data-testid="hero-subtext">
              Slidin' Deliciously — fresh, made-to-order and quick, delivered straight to your door.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link
                to="/order"
                className="sticker-btn px-7 py-4 font-display uppercase text-base"
                style={{ background: "var(--goat-orange)", color: "var(--goat-white)", borderColor: "var(--goat-white)", boxShadow: "4px 4px 0px var(--goat-white)" }}
                data-testid="hero-order-btn"
              >
                Order Now
              </Link>
              <Link
                to="/menu"
                className="sticker-btn px-7 py-4 font-display uppercase text-base"
                style={{ background: "var(--goat-cream)", color: "var(--goat-green)" }}
                data-testid="hero-menu-btn"
              >
                See The Menu
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Ticker */}
      <div className="overflow-hidden py-3" style={{ background: "var(--goat-orange)", borderTop: "3px solid var(--goat-green)", borderBottom: "3px solid var(--goat-green)" }} data-testid="ticker" aria-hidden="true">
        <div className="ticker-track">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex shrink-0 items-center">
              {["Fresh • Made-To-Order • Quick", "Sliders From Just ₹139", "Slidin' Deliciously", "Party Crates For Your Crew"].map((msg) => (
                <span key={msg} className="flex items-center font-display uppercase text-base md:text-lg tracking-wide whitespace-nowrap" style={{ color: "var(--goat-white)" }}>
                  <span className="px-6">{msg}</span>
                  <span style={{ color: "var(--goat-cream)" }}>★</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Our Story — teaser */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="story-section">
        <span className="badge-sticker inline-block px-5 py-2 text-sm font-semibold uppercase tracking-widest mb-6" style={{ background: "var(--goat-orange)", color: "var(--goat-white)" }} data-testid="story-badge">
          Our Story
        </span>
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl uppercase" style={{ color: "var(--goat-green)" }} data-testid="story-heading">
              G.O.A.T Sliders
            </h2>
            <p className="mt-6 text-lg md:text-xl" style={{ color: "var(--goat-green)" }} data-testid="story-text-1">
              Every food brand has a beginning. G.O.A.T Sliders started with a long-standing dream of creating something of our own in the world of food.
            </p>
            <p className="mt-4 text-lg md:text-xl" style={{ color: "var(--goat-green)" }} data-testid="story-teaser">
              Soft toasted pav, flavourful fillings, homemade sauces and generous amounts of cheese — small bites, never small flavours.
            </p>
            <Link
              to="/story"
              className="sticker-btn inline-flex items-center gap-2 mt-7 px-7 py-4 font-display uppercase text-base"
              style={{ background: "var(--goat-green)", color: "var(--goat-cream)" }}
              data-testid="story-read-more-btn"
            >
              Read Our Full Story <ArrowRight size={18} />
            </Link>
          </div>
          <div className="sticker-card sticker-card-orange overflow-hidden p-0 min-h-72">
            <img src={IMAGES.veg} alt="Fresh GOAT sliders" className="w-full h-80 object-cover" data-testid="story-image" />
          </div>
        </div>
      </section>

      {/* Video feature */}
      <section className="py-16" style={{ background: "var(--goat-green)" }} data-testid="video-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div className="order-2 md:order-1">
            <span className="badge-sticker inline-block px-5 py-2 text-sm font-semibold uppercase tracking-widest mb-6" style={{ background: "var(--goat-cream)", color: "var(--goat-green)", borderColor: "var(--goat-cream)", boxShadow: "3px 3px 0px var(--goat-orange)" }} data-testid="video-badge">
              Straight From The Grill
            </span>
            <h2 className="text-4xl md:text-5xl uppercase" style={{ color: "var(--goat-cream)" }} data-testid="video-heading">
              Watch The Cheese Pull
            </h2>
            <p className="mt-6 text-lg md:text-xl" style={{ color: "var(--goat-cream)" }} data-testid="video-text">
              Soft toasted pav, homemade sauces and a generous layer of molten cheese — this is what every GOAT slider looks like fresh off the grill.
            </p>
            <Link
              to="/order"
              className="sticker-btn inline-block mt-8 px-7 py-4 font-display uppercase text-base"
              style={{ background: "var(--goat-orange)", color: "var(--goat-white)", borderColor: "var(--goat-white)", boxShadow: "4px 4px 0px var(--goat-white)" }}
              data-testid="video-order-btn"
            >
              Taste It Yourself
            </Link>
          </div>
          <div className="order-1 md:order-2 sticker-card overflow-hidden p-0 mx-auto w-full max-w-sm" style={{ borderColor: "var(--goat-white)", boxShadow: "6px 6px 0px var(--goat-orange)" }}>
            <video
              src="/assets/goat-video.mp4"
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-auto block"
              data-testid="brand-video"
            />
          </div>
        </div>
      </section>

      {/* Menu teaser — category cards */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="menu-teaser-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="badge-sticker inline-block px-5 py-2 text-sm font-semibold uppercase tracking-widest mb-4" style={{ background: "var(--goat-orange)", color: "var(--goat-white)" }} data-testid="menu-teaser-badge">
              Sliders &amp; More
            </span>
            <h2 className="text-4xl md:text-5xl uppercase" style={{ color: "var(--goat-green)" }} data-testid="menu-teaser-heading">
              What's Cooking
            </h2>
          </div>
          <Link to="/menu" className="flex items-center gap-2 font-semibold uppercase text-sm tracking-wide" style={{ color: "var(--goat-orange)" }} data-testid="menu-teaser-link">
            Full menu <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {MENU_CARDS.map((card) => (
            <Link key={card.key} to="/menu" className="sticker-card overflow-hidden p-0 block" data-testid={`menu-card-${card.key}`}>
              <img src={card.image} alt={card.label} className="w-full h-44 object-cover" />
              <div className="p-5">
                <h3 className="text-xl uppercase" style={{ color: "var(--goat-green)" }}>{card.label}</h3>
                <p className="mt-1 text-base" style={{ color: "var(--goat-green)" }}>{card.blurb}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Why GOAT */}
      <section className="py-16" style={{ background: "var(--goat-green)" }} data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-4xl md:text-5xl uppercase mb-12" style={{ color: "var(--goat-cream)" }} data-testid="why-heading">
            Why GOAT?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY.map((w) => (
              <div key={w.title} className="sticker-card sticker-card-orange p-8" data-testid={`why-card-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="badge-sticker inline-flex p-3 mb-5" style={{ background: "var(--goat-orange)" }}>
                  <w.icon size={26} color="#FFFFFF" />
                </div>
                <h3 className="text-2xl uppercase mb-3" style={{ color: "var(--goat-green)" }}>{w.title}</h3>
                <p className="text-base md:text-lg" style={{ color: "var(--goat-green)" }}>{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Party Crates */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center" data-testid="party-section">
        <div>
          <span className="badge-sticker inline-block px-5 py-2 text-sm font-semibold uppercase tracking-widest mb-6" style={{ background: "var(--goat-orange)", color: "var(--goat-white)" }} data-testid="party-badge">
            Offices • Parties • Events
          </span>
          <h2 className="text-4xl md:text-5xl uppercase" style={{ color: "var(--goat-green)" }} data-testid="party-heading">
            Party Crates
          </h2>
          <p className="mt-6 text-lg md:text-xl" style={{ color: "var(--goat-green)" }} data-testid="party-text">
            Feeding a whole office or a full house party? Our slider crates are built for crowds — mix and match veg and non-veg sliders, add fries, nachos and cold brews, and we'll have it all made fresh and delivered quick.
          </p>
          <p className="mt-4 text-lg md:text-xl" style={{ color: "var(--goat-green)" }} data-testid="party-text-2">
            Tell us your headcount and budget on WhatsApp, and we'll put together a crate that works.
          </p>
          <a
            href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi GOAT Sliders! I'd like to plan a Party Crate. Headcount: ")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="sticker-btn inline-flex items-center gap-2 mt-8 px-7 py-4 font-display uppercase text-base"
            style={{ background: "var(--goat-orange)", color: "var(--goat-white)" }}
            data-testid="party-whatsapp-btn"
            aria-label="Plan a party crate on WhatsApp"
          >
            Plan My Crate <ArrowRight size={18} />
          </a>
        </div>
        <div className="relative">
          <div className="sticker-card sticker-card-orange overflow-hidden p-0">
            <img src={IMAGES.party} alt="Party crate of GOAT sliders" className="w-full h-80 object-cover" data-testid="party-image" />
          </div>
          <span className="badge-sticker absolute -bottom-4 left-6 px-4 py-2 font-display uppercase text-sm -rotate-3" style={{ background: "var(--goat-green)", color: "var(--goat-cream)" }} data-testid="party-quote-badge">
            Custom quotes for 10+ people
          </span>
        </div>
      </section>

      {/* Contact teaser */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20" data-testid="contact-teaser-section">
        <div className="sticker-card sticker-card-orange p-10 md:p-14 text-center" style={{ background: "var(--goat-green)" }}>
          <h2 className="text-4xl md:text-5xl uppercase" style={{ color: "var(--goat-cream)" }} data-testid="contact-teaser-heading">
            Craving a slider?
          </h2>
          <p className="mt-4 text-lg md:text-xl" style={{ color: "var(--goat-cream)" }}>
            Order now — fresh off the grill, quick to your door.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/order"
              className="sticker-btn px-8 py-4 font-display uppercase text-base"
              style={{ background: "var(--goat-orange)", color: "var(--goat-white)", borderColor: "var(--goat-white)", boxShadow: "4px 4px 0px var(--goat-white)" }}
              data-testid="contact-teaser-order-btn"
            >
              Place Your Order
            </Link>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi GOAT Sliders! I'd like to order.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="sticker-btn px-7 py-4 flex items-center gap-2 font-semibold uppercase text-sm"
              style={{ background: "var(--goat-white)", color: "var(--goat-green)" }}
              data-testid="contact-teaser-whatsapp-btn"
              aria-label="Chat on WhatsApp"
            >
              <MessageCircle size={18} /> WhatsApp Us
            </a>
            <a
              href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
              className="sticker-btn px-7 py-4 flex items-center gap-2 font-semibold uppercase text-sm"
              style={{ background: "var(--goat-white)", color: "var(--goat-green)" }}
              data-testid="contact-teaser-call-btn"
              aria-label="Call us"
            >
              <Phone size={18} /> Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
